import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../config";
import { toggleModal } from "../../../reducers/modalReducer";
import { useDispatch } from "react-redux";


const AdminCompanyDetails = ({ id, setPlan }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [providesCFOServices, setProvidesCFOServices] = useState(false);

    const dropdownRefs = useRef({});

    useEffect(() => {
        if (id) {
            fetchCompanyDetails();
        }
    }, [id]);

    const fetchCompanyDetails = async () => {
        try {
            setLoading(true);
            const response = await getRequest(`/admin/company/${id}`);

            if (response && response.code === 200) {
                setCompany(response.body);
                setProvidesCFOServices(response.body.providesCFOServices || false);
            } else {
                setError("Company not found");
            }
        } catch (err) {
            setError("Failed to load company details");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToAdmin = () => {
        navigate("/admin");
    };

    const handleCFOServicesToggle = async (e) => {
        const newValue = e.target.checked;
        setProvidesCFOServices(newValue);

        try {
            const response = await postRequest(`/admin/company/${id}`, {
                providesCFOServices: newValue
            });

            if (response && response.code === 200) {
                setCompany(prev => ({
                    ...prev,
                    providesCFOServices: newValue
                }));
            }
        } catch (err) {
            setProvidesCFOServices(!newValue);
            setError("Failed to update CFO services status");
        }
    };

    const toggleDropdown = (companyId) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [companyId]: !prev[companyId]
        }));
    };

    const getAvailablePlans = (company) => {
        // If company has servicedBy, only show Pro
        console.log(company.servicedBy && (company.servicedBy['role'] !== "User" && company.servicedBy['role'] !== "Member"))
        if (company.servicedBy && (company.servicedBy['role'] !== "User" && company.servicedBy['role'] !== "Member")
        ) {
            return ['Pro'];
        }

        // If subscription is Free, show Lite and Pro
        if (company.subscription === 'Free') {
            return ['Lite', 'Pro'];
        }

        // If subscription is Lite, only show Pro
        if (company.subscription === 'Lite') {
            return ['Pro'];
        }

        // For any other case (undefined, null, etc.), show Lite and Pro
        return ['Lite', 'Pro'];
    };

    const handleUpgrade = (company, plan) => {
        setPlan(plan);
        setOpenDropdowns(prev => ({
            ...prev,
            [company._id]: false
        }));
        dispatch(toggleModal({ open: 'subscriptionPaymentConfirm' }))
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(dropdownRefs.current).forEach(companyId => {
                if (dropdownRefs.current[companyId] && !dropdownRefs.current[companyId].contains(event.target)) {
                    setOpenDropdowns(prev => ({
                        ...prev,
                        [companyId]: false
                    }));
                }
            });
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Create a function to render the subscription upgrade cell
    const renderSubscriptionCell = (company) => {
        const companyId = company._id;
        const isDropdownOpen = openDropdowns[companyId] || false;
        const availablePlans = getAvailablePlans(company);

        // If subscription is Pro, show disabled badge
        if (company.subscription === "Pro") {
            return (
                <div className="adminCompanyDetailPage-subscription-badge adminCompanyDetailPage-pro">
                    <i className="fa fa-crown"></i>
                    <span>Pro</span>
                </div>
            );
        }

        // For all other cases, show interactive dropdown
        return (
            <div
                ref={el => dropdownRefs.current[companyId] = el}
                className="adminCompanyDetailPage-subscription-dropdown"
            >
                <div
                    className="adminCompanyDetailPage-subscription-upgrade-btn"
                    onClick={() => toggleDropdown(companyId)}
                >
                    <i className="fa fa-arrow-up"></i>
                    <span>Upgrade</span>
                    <i
                        className={`fa fa-chevron-${isDropdownOpen ? 'up' : 'down'}`}
                    />
                </div>

                {isDropdownOpen && (
                    <div className="adminCompanyDetailPage-subscription-dropdown-menu">
                        {availablePlans.map((plan, index) => (
                            <div
                                key={plan}
                                className="adminCompanyDetailPage-subscription-option"
                                onClick={() => handleUpgrade(company, plan)}
                            >
                                <span>{plan}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="df fdc aic jcc" style={{ minHeight: "50vh" }}>
                <div className="fs1-5rem c4">Loading company details...</div>
            </div>
        );
    }

    if (error || !company) {
        return (
            <div className="df fdc aic jcc" style={{ minHeight: "50vh" }}>
                <div className="fs1-5rem c9 mb2rem">{error || "Company not found"}</div>
                <button
                    onClick={handleBackToAdmin}
                    className="bg4 c1 px2rem py1rem br5px cp"
                >
                    Back to Admin
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="adminCompanyDetailPage-company-details-modal">
                {/* Modern Header with Gradient */}
                <div className="adminCompanyDetailPage-modal-header">
                    <div className="adminCompanyDetailPage-header-content">
                        <div className="adminCompanyDetailPage-company-avatar">
                            <div className="adminCompanyDetailPage-avatar-circle">
                                <span className="adminCompanyDetailPage-avatar-text">
                                    {company.cName?.[0]?.toUpperCase() || 'C'}
                                </span>
                            </div>
                        </div>
                        <div className="adminCompanyDetailPage-header-info">
                            <h1 className="adminCompanyDetailPage-company-title">{company.cName || 'Unknown Company'}</h1>
                            <p className="adminCompanyDetailPage-company-subtitle">Company Details</p>
                        </div>
                    </div>
                </div>

                {/* Main Content with Modern Cards */}
                <div className="adminCompanyDetailPage-modal-content">

                    {/* CFO Services Toggle Card */}
                    <div className="adminCompanyDetailPage-cfo-services-card">
                        <div className="adminCompanyDetailPage-cfo-card-content">
                            <div className="adminCompanyDetailPage-cfo-icon-wrapper">
                                <div className="adminCompanyDetailPage-cfo-icon">
                                    <i className="fa fa-briefcase"></i>
                                </div>
                            </div>
                            <div className="adminCompanyDetailPage-cfo-info">
                                <h4 className="adminCompanyDetailPage-cfo-title">CFO Services</h4>
                                <p className="adminCompanyDetailPage-cfo-description">
                                    Enable if this company provides Chief Financial Officer services
                                </p>
                            </div>
                            <label className="adminCompanyDetailPage-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={providesCFOServices}
                                    onChange={handleCFOServicesToggle}
                                />
                                <span className="adminCompanyDetailPage-toggle-slider"></span>
                            </label>
                        </div>
                    </div>


                    <div className="adminCompanyDetailPage-info-grid">
                        {/* Company Information Card */}
                        <div className="adminCompanyDetailPage-info-card">
                            <div className="adminCompanyDetailPage-card-header">
                                <div className="adminCompanyDetailPage-card-icon">
                                    <i className="fa fa-building"></i>
                                </div>
                                <h3 className="adminCompanyDetailPage-card-title">Company Information</h3>
                            </div>
                            <div className="adminCompanyDetailPage-card-content">
                                <div className="adminCompanyDetailPage-info-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-hashtag"></i>
                                        <span>Company Number</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-info-value">{company['ukRegNo'] || '-'}</div>
                                </div>

                                <div className="adminCompanyDetailPage-info-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-bullseye"></i>
                                        <span>Company Objective</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-info-value">{company.cType || '-'}</div>
                                </div>

                                <div className="adminCompanyDetailPage-info-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-building"></i>
                                        <span>Company Stage</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-info-value adminCompanyDetailPage-stage-badge">{company.cStage || '-'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Team & Subscription Card */}
                        <div className="adminCompanyDetailPage-info-card">
                            <div className="adminCompanyDetailPage-card-header">
                                <div className="adminCompanyDetailPage-card-icon">
                                    <i className="fa fa-users"></i>
                                </div>
                                <h3 className="adminCompanyDetailPage-card-title">Team & Subscription</h3>
                            </div>
                            <div className="adminCompanyDetailPage-card-content">
                                <div className="adminCompanyDetailPage-info-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-user-plus"></i>
                                        <span>Created By</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-creator-info">
                                        <div className="adminCompanyDetailPage-creator-avatar">
                                            <span className="adminCompanyDetailPage-creator-initials">
                                                {company.creatorId?.firstName?.[0]}{company.creatorId?.lastName?.[0]}
                                            </span>
                                        </div>
                                        <span className="adminCompanyDetailPage-creator-name">
                                            {company.creatorId?.firstName} {company.creatorId?.lastName}
                                        </span>
                                    </div>
                                </div>

                                <div className="adminCompanyDetailPage-info-item adminCompanyDetailPage-subscription-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-credit-card"></i>
                                        <span>Subscription</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-subscription-wrapper">
                                        {renderSubscriptionCell(company)}
                                    </div>
                                </div>

                                <div className="adminCompanyDetailPage-info-item">
                                    <div className="adminCompanyDetailPage-info-label">
                                        <i className="fa fa-certificate"></i>
                                        <span>Certificate</span>
                                    </div>
                                    <div className="adminCompanyDetailPage-data-room-status">
                                        <span className="adminCompanyDetailPage-status-indicator adminCompanyDetailPage-disconnected"></span>
                                        <span className="adminCompanyDetailPage-status-text">{company['certificateNext'] || "-"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminCompanyDetails;
